import { useEffect, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '../primitives';

export interface PronounsFieldLabels {
  /** e.g. "Pronouns" */
  title: string;
  /** e.g. "Shown beside your name so colleagues address you correctly." */
  description?: string;
  placeholder: string;
  save: string;
  /** Shown when the request failed. */
  failed: string;
}

export interface PronounsFieldProps {
  /** Current value, or empty when unset. */
  value: string;
  onSave: (pronouns: string) => Promise<void>;
  labels: PronounsFieldLabels;
}

/**
 * Set or clear your own pronouns.
 *
 * Free text rather than a list of options, deliberately. Every enumeration of
 * pronouns leaves somebody out, and there is nothing to gain by constraining
 * it: this is displayed beside a name, never matched on or branched on.
 *
 * Only ever your own. There is no equivalent control anywhere an operator
 * edits somebody else's record, and none on the officials roster at all —
 * those are people who never sign in, so there is nobody there to declare
 * anything, and a value typed on their behalf would be a guess about a
 * stranger printed on a statutory record.
 *
 * Clearing it is saving an empty field, which is why there is no separate
 * Remove: "I would rather not say" and "I have not got round to it" should
 * not be two different buttons.
 */
export function PronounsField({ value, onSave, labels }: PronounsFieldProps) {
  const [draft, setDraft] = useState(value);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // Follows the server when the account reloads; the local copy only leads
  // between a save being typed and the next read.
  useEffect(() => setDraft(value), [value]);

  const dirty = draft.trim() !== value.trim();

  return (
    <Box>
      <Typography sx={{ fontWeight: 600, fontSize: 14.5 }}>{labels.title}</Typography>
      {labels.description && (
        <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 0.5 }}>
          {labels.description}
        </Typography>
      )}
      <Stack direction="row" spacing={1.5} sx={{ mt: 1.5, alignItems: 'flex-start' }}>
        <TextField
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setFailed(false);
          }}
          placeholder={labels.placeholder}
          slotProps={{ htmlInput: { maxLength: 40 } }}
          sx={{ maxWidth: 280 }}
          fullWidth
        />
        <Button
          variant="contained"
          disabled={!dirty || busy}
          onClick={async () => {
            setBusy(true);
            setFailed(false);
            try {
              await onSave(draft.trim());
            } catch {
              setFailed(true);
            } finally {
              setBusy(false);
            }
          }}
          sx={{ mt: 0.5 }}
        >
          {labels.save}
        </Button>
      </Stack>
      {failed && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {labels.failed}
        </Alert>
      )}
    </Box>
  );
}
